import {
  IntegrationProvider,
  isRecordingS3IntakeIntegrationSettings,
} from "@repo/app-types/entities/integration";
import { useCopyAction } from "@repo/ui/clipboard/useCopyAction";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import type { FC } from "react";
import { integrationConfigs } from "./integrationConfigs.js";
import { RecordingS3IntakeAgentEmailMapping } from "./recordingS3IntakeAgentEmailMapping.js";
import { useRecordingS3IntakeDetailOutletContext } from "./recordingS3IntakeIntegrationDetailSection.js";

const config = integrationConfigs.find(
  (entry) => entry.provider === IntegrationProvider.RECORDING_S3_INTAKE,
);

/** A read-only value with a copy-to-clipboard control, for the bucket + key. */
const CopyableValue: FC<{ value: string; label: string }> = ({
  value,
  label,
}) => {
  const { didJustCopy, onCopy } = useCopyAction(value);
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 rounded-md border border-border-light bg-muted px-2.5 py-1.5 font-mono text-xs break-all text-foreground">
        {value}
      </code>
      <button
        type="button"
        onClick={onCopy}
        aria-label={`Copy ${label}`}
        className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
      >
        {didJustCopy ? (
          <IconCheck className="size-4 text-success" />
        ) : (
          <IconCopy className="size-4" />
        )}
      </button>
    </div>
  );
};

/**
 * The "Recording S3 intake" Settings tab: read-only delivery details — the S3
 * bucket recordings are delivered to and an example of the expected file-name
 * layout — plus, for RingCX customers, the agent email mapping. There is no
 * connect/disconnect: the surface exists as long as the company is an intake
 * customer.
 */
export const RecordingS3IntakeSettingsTab: FC = () => {
  const { integration } = useRecordingS3IntakeDetailOutletContext();
  const settings = isRecordingS3IntakeIntegrationSettings(integration.settings)
    ? integration.settings
    : null;

  return (
    <div className="space-y-6">
      <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
        {config?.description}
      </p>

      {settings ? (
        <div className="max-w-3xl space-y-5">
          <div className="space-y-1.5">
            <p className="text-sm font-medium text-foreground">Bucket</p>
            <p className="text-sm text-muted-foreground">
              Deliver call recordings to this Alpharun-owned S3 bucket.
            </p>
            <CopyableValue value={settings.bucket} label="bucket name" />
          </div>

          <div className="space-y-1.5">
            <p className="text-sm font-medium text-foreground">
              Example file name
            </p>
            <p className="text-sm text-muted-foreground">
              An example of the file-name layout we expect. Actual values (the
              agent, number, and timestamp) vary per recording.
            </p>
            <CopyableValue
              value={settings.example_object_key}
              label="example file name"
            />
          </div>

          {settings.agent_mapping_source === "ringcx" ? (
            <RecordingS3IntakeAgentEmailMapping />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
